/************
 * Miss Mary Algolia → Grease mapper
 * eslint rules: allow unused params for compatibility signatures
 ************/
/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  AlgoliaCentraShopApiProduct,
  AlgoliaShopApiProductQueryContext,
  AlgoliaShopApiProductToGreaseProductOptions,
  getProductCategories,
  getExtraAttributesForProduct,
} from "@made-people/grease-algolia";
import {
  Product,
  ProductBase,
  ProductMediaImage,
  ProductVariant,
  ProductVariantDimension,
  ProductPurchaseInfo,
  ProductContent,
  ProductSibling,
  Attribute,
} from "@made-people/grease-product";
import {
  dimensionInfo,
  productImageKey,
} from "../centra/mappers/utils/get-centra-mapper-options";
import { mmColorSwatchSchema } from "../centra/mappers/mm-product-extra-attributes";
import { getSizeChartByCountryCode } from "../centra/mappers/utils/get-sizechart-by-country-code";

export const itemIdDimensionParser = {
  delimiter: "x",
  dimensionInfo,
  productImageKey,
};

// ---------------------------------
// Extra Miss Mary specific types
// ---------------------------------

type MissMarySwatch = {
  id: string;
  sku: string;
  url?: string;
  name: string;
  swatch_img: string;
};

type PriceListEntry = {
  price: string;
  priceBeforeDiscount: string;
  priceAsNumber: number;
  priceBeforeDiscountAsNumber: number;
};

type MMAlgoliaProduct = AlgoliaCentraShopApiProduct & {
  mainImage?: string;
  secondaryImage?: string;
  excerpt?: string;
  badge?: Record<string, string>;
  variant_badge?: Record<string, string>;
  swatches?: MissMarySwatch[];
  sizechart?: Record<string, { label: string; value: string }[]>;
  pricesByPricelist?: Record<string, PriceListEntry>;
};

/**
 * ----------------------
 * Constants & helpers
 * ----------------------
 */
const DEFAULT_DIMENSION_KEYS = {
  key: "size",
  label: "Item_Dimension_Size",
};

/**
 * Media helpers
 */
function missMaryAlgoliaCentraShopProductMediaImages(
  product: MMAlgoliaProduct,
): ProductMediaImage[] {
  const images: ProductMediaImage[] = [];
  if (typeof product.mainImage === "string") {
    images.push({
      type: "image",
      src: product.mainImage,
      alt: "",
    });
  }
  if (typeof product.secondaryImage === "string") {
    images.push({
      type: "image",
      src: product.secondaryImage,
      alt: "",
    });
  }
  return images;
}

/**
 * Build badges from both the product "badge" attribute and the variant-level
 * "variant_badge" attribute (e.g. "Ny färg" / "New colour").
 */
function missMaryAlgoliaProductBadges(
  product: MMAlgoliaProduct,
): ProductContent["badges"] {
  return [
    ...Object.values(product.badge ?? {}),
    ...Object.values(product.variant_badge ?? {}),
  ].map((badge) => ({
    label: badge,
    badgeType: "unknown" as const,
  }));
}

/**
 * Content builder (name, description, media etc.)
 */
function missMaryAlgoliaCentraShopApiProductContent(
  product: MMAlgoliaProduct,
): ProductContent {
  return {
    name: product.name,
    description: product.description,
    shortDescription: product.excerpt ?? "",
    media: missMaryAlgoliaCentraShopProductMediaImages(product),
    badges: missMaryAlgoliaProductBadges(product),
  };
}

/**
 * Base product mapping (same structure as ProductBase)
 */
export const missMaryShopApiProductToGreaseProductBase = (
  product: MMAlgoliaProduct,
): ProductBase => {
  if (typeof product.url !== "string") {
    throw new Error("Product URL is required");
  }

  return {
    productId: product.product,
    productKey: product.sku,
    slug: product.url,
    discontinued: false,
    content: missMaryAlgoliaCentraShopApiProductContent(product),
    attributes: mmAlgoliaGetExtraAttributesForProduct(product),
    categories: getProductCategories(product),
  };
};

export const mmAlgoliaGetExtraAttributesForProduct = (
  product: MMAlgoliaProduct,
): Attribute[] => {
  return [
    ...getExtraAttributesForProduct(product),
    {
      attributeKey: "product_parent_id",
      attributeLabel: "Product Parent ID",
      value: product.productSku,
      valueLabel: product.productSku,
    },
    // {
    //   attributeKey: "product_main_category",
    //   attributeLabel: "Product Main Category",
    //   value: product.categoryUri,
    //   valueLabel: product.categoryName?.join(" / ") ?? "Miss Mary of Sweden",
    // },
    {
      attributeKey: "product_variant_name",
      attributeLabel: "Product Variant Name",
      value: product.variantName,
      valueLabel: product.variantName,
    },
  ];
};

/**
 * Enrich base product with colour swatch & badges specific for Miss Mary
 */
export const missMaryShopToGreaseBaseProduct = (
  greaseBaseProduct: ProductBase,
  product: MMAlgoliaProduct,
): ProductBase => {
  const colorSwatchData = mmColorSwatchSchema.safeParse(product.color_swatch);

  if (!colorSwatchData.success) {
    return greaseBaseProduct;
  }

  return {
    ...greaseBaseProduct,
    content: {
      ...greaseBaseProduct.content,
      badges: missMaryAlgoliaProductBadges(product),
    },
    attributes: [
      ...greaseBaseProduct.attributes,
      {
        attributeKey: "product_color_image_url",
        attributeLabel: "Product Color Image URL",
        value: colorSwatchData.data.image.url,
        valueLabel: colorSwatchData.data.description,
      },
    ],
  };
};

function getPriceInfoFromProduct(
  product: MMAlgoliaProduct,
  activePricelist: string,
): ProductPurchaseInfo["priceInfo"] {
  const priceEntry =
    product.pricesByPricelist?.[activePricelist] ??
    Object.values(product.pricesByPricelist ?? {})[0];

  if (!priceEntry) {
    return {
      price: { formatted: "", number: 0 },
      priceWithoutDiscount: { formatted: "", number: 0 },
      previouslyDiscountedPrices: [],
    };
  }

  const isDiscounted =
    priceEntry.priceAsNumber < priceEntry.priceBeforeDiscountAsNumber;
  const discountPercent = isDiscounted
    ? Math.round(
        (1 -
          priceEntry.priceAsNumber / priceEntry.priceBeforeDiscountAsNumber) *
          100,
      )
    : 0;

  return {
    price: {
      formatted: priceEntry.price,
      number: priceEntry.priceAsNumber,
    },
    priceWithoutDiscount: {
      formatted: priceEntry.priceBeforeDiscount,
      number: priceEntry.priceBeforeDiscountAsNumber,
    },
    previouslyDiscountedPrices: [],
    discountInfo: isDiscounted
      ? {
          percent: discountPercent,
        }
      : undefined,
  };
}

function buildVariantsFromSizeChart(
  product: MMAlgoliaProduct,
  options: AlgoliaShopApiProductToGreaseProductOptions,
  countryCode: string,
  activePricelist: string,
): ProductVariant[] {
  if (typeof product.sizechart !== "object") {
    return [];
  }

  const sizechartObj = (product.sizechart ?? {}) as Record<
    string,
    { label: string; value: string; inStock: boolean }[]
  >;

  const activeSizeChartKey = getSizeChartByCountryCode(countryCode);
  const entries =
    sizechartObj[activeSizeChartKey] ?? sizechartObj["sizechart_eu"] ?? [];

  return entries.map((entry) => {
    const label: string = entry.label;
    const itemValue: string = entry.value;

    const dimensions = getVariantDimensions(
      product,
      entry,
      options,
      countryCode,
    );

    return {
      sku: itemValue,
      inventory: {
        quantity: entry.inStock ? 9999 : 0,
        inStock: entry.inStock,
        warehouseId: "1",
      },
      additionalInventory: [],
      priceInfo: getPriceInfoFromProduct(product, activePricelist),

      mainProductId: product.product,
      mainProductKey: product.product,
      variantId: itemValue,
      variantKey: itemValue,
      label,
      dimensions,
    } as ProductVariant;
  });
}

/**
 * Build sibling products from swatches array.
 */
function buildSiblingProductsFromSwatches(
  product: MMAlgoliaProduct,
): ProductSibling[] {
  const swatches = product.swatches ?? [];

  return swatches.map((swatch) => {
    const slug = swatch.url || product.url;
    if (typeof slug !== "string") {
      throw new Error("Product URL is required");
    }

    return missMaryShopToGreaseBaseProduct(
      {
        productId: swatch.id,
        productKey: swatch.sku,
        slug,
        discontinued: false,
        content: {
          name: product.name,
          description: product.description,
          shortDescription: product.excerpt ?? "",
          media: [
            {
              type: "image",
              src: swatch.swatch_img,
              alt: "",
            },
          ],
          badges: [],
        },
        attributes: [
          {
            attributeKey: "product_color_image_url",
            attributeLabel: "Product Color Image URL",
            value: swatch.swatch_img,
            valueLabel: swatch.name,
          },
        ],
        categories: getProductCategories(product),
      },
      product,
    );
  });
}

export function mmAlgoliaCentraShopProductMapper(
  product: MMAlgoliaProduct,
  queryContext: AlgoliaShopApiProductQueryContext,
  options: AlgoliaShopApiProductToGreaseProductOptions,
  countryCode: string,
): Product | undefined {
  try {
    const baseProduct = missMaryShopToGreaseBaseProduct(
      missMaryShopApiProductToGreaseProductBase(product),
      product,
    );

    const variants = buildVariantsFromSizeChart(
      product,
      options,
      countryCode,
      queryContext.pricelist,
    );
    const siblingProducts = buildSiblingProductsFromSwatches(product);

    if (variants.length === 1) {
      const singleVariant = variants[0];
      return {
        dataModel: "simple",
        base: baseProduct,
        siblingProducts,
        sku: singleVariant.sku,
        inventory: singleVariant.inventory,
        additionalInventory: singleVariant.additionalInventory,
        priceInfo: singleVariant.priceInfo,
        isBundle: false,
        itemId: singleVariant.variantId,
      };
    } else if (variants.length > 1) {
      return {
        dataModel: "with_variants",
        base: baseProduct,
        siblingProducts,
        variants,
      };
    } else {
      return undefined;
    }
  } catch (error) {
    console.error(
      `mmAlgoliaCentraShopProductMapper: Error while converting product with sku "${product?.sku ?? "unknown"}"`,
      error,
    );
    return undefined;
  }
}

/**
 * ----------------------
 * (Optional) helper if you want to keep the generic dimension parser – not used
 * in the current implementation but left here for future needs.
 */
export const getVariantDimensions = (
  product: MMAlgoliaProduct,
  entry: { label: string; value: string },
  options: AlgoliaShopApiProductToGreaseProductOptions,
  countryCode: string,
): ProductVariantDimension[] => {
  const parser = options.itemIdDimensionParser;

  const sizeLabel = entry.label;

  if (!parser) {
    return [createDefaultDimension(sizeLabel)];
  }

  const dimensions = sizeLabel.split(parser.delimiter);

  if (dimensions.length === 1) {
    return [createDefaultDimension(sizeLabel)];
  }

  return dimensions.map((value, index) =>
    createDimensionFromValue(value, parser.dimensionInfo[index]),
  );
};

const createDefaultDimension = (label: string): ProductVariantDimension => ({
  value: label,
  valueLabel: label,
  dimensionKey: DEFAULT_DIMENSION_KEYS.key,
  dimensionLabel: DEFAULT_DIMENSION_KEYS.label,
});

const createDimensionFromValue = (
  value: string,
  dimensionInfo: { key: string; label: string } | undefined,
): ProductVariantDimension => ({
  value,
  valueLabel: value,
  dimensionKey: dimensionInfo?.key ?? DEFAULT_DIMENSION_KEYS.key,
  dimensionLabel: dimensionInfo?.label ?? DEFAULT_DIMENSION_KEYS.label,
});
